/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Three-dot overflow action menu component.
 *
 * @file ThreeDotActionMenu.jsx
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Menu,
  MenuItem,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Icon from '../icons/Icon';
import { ThreeDotActionMenuStylesRoot, classes, scopedStyles } from './threeDotActionMenuStyles';

/**
 * The menu surface renders in a portal, outside the subtree the wrapper scopes,
 * so the menu carries the scope itself.
 */
const ScopedMenu = styled(Menu)(scopedStyles);

const ThreeDotActionMenu = ({
  menuItems,
  ariaLabel,
  onOpenChange
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  if (!menuItems.length) {
    return null;
  }

  const handleMenuClose = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setMenuAnchorEl(null);
    onOpenChange?.(false);
  };

  const handleMenuOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    onOpenChange?.(true);
  };

  return (
    <Fragment>
      <ThreeDotActionMenuStylesRoot className={classes.menuWrapper}>
        <IconButton
          className={`${classes.menuIconButton} ${isMenuOpen ? classes.activeMenuIcon : ''}`}
          onClick={handleMenuOpen}
          aria-label={ariaLabel}
          aria-haspopup="true"
          aria-expanded={isMenuOpen}
        >
          <Icon name="ThreeDotMenuIcon" />
        </IconButton>
      </ThreeDotActionMenuStylesRoot>
      <ScopedMenu
        anchorEl={menuAnchorEl}
        keepMounted
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        classes={{ paper: classes.menuPaper, list: classes.menuList }}
      >
        {menuItems.map((menuItem) => (
          <MenuItem
            key={menuItem.id}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleMenuClose();

              menuItem.onClick?.();
            }}
            className={`${classes.menuItem} ${menuItem.divider ? classes.menuItemDivider : ''}`}
          >
            <Typography>{menuItem.label}</Typography>
          </MenuItem>
        ))}
      </ScopedMenu>
    </Fragment>
  );
};

ThreeDotActionMenu.propTypes = {
  menuItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    divider: PropTypes.bool
  })),
  ariaLabel: PropTypes.string,
  onOpenChange: PropTypes.func
};

ThreeDotActionMenu.defaultProps = {
  menuItems: [],
  ariaLabel: 'Open menu',
  onOpenChange: null
};

export default ThreeDotActionMenu;
